import type { Profile } from "@bioflow/contracts";
import { CheckCircle2 } from "lucide-react";
import { ContentIcon, Molecule } from "../../components/Icons";
import { ProductShot } from "../../components/ProductShot";
import { done, effect, how, start, why } from "../../content/onboarding";
import { GOAL_LABELS, formatBmi } from "../../lib/profile";

/**
 * Содержимое информационных экранов онбординга (1–4 и 7). Кнопки живут в
 * OnboardingScreen — здесь только то, что человек читает.
 */
export function StartStep() {
  return (
    <div className="onb__center">
      <ProductShot size="hero" />
      <h1 className="onb__title">{start.title}</h1>
      <p className="onb__subtitle">{start.subtitle}</p>
    </div>
  );
}

export function HowStep() {
  return (
    <>
      <div className="onb__visual">
        <Molecule />
      </div>
      <h1 className="onb__title">{how.title}</h1>
      <p className="onb__subtitle">{how.body}</p>
      <div className="cards" style={{ marginTop: 18 }}>
        {how.cards.map((card) => (
          <div key={card.title} className="card">
            <div className="card__icon">
              <ContentIcon name={card.icon} size={18} />
            </div>
            <div>
              <div className="card__title">{card.title}</div>
              <div className="card__body">{card.body}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export function EffectStep() {
  return (
    <>
      <h1 className="onb__title">{effect.title}</h1>
      <ol className="timeline">
        {effect.timeline.map((stage, index) => (
          <li key={stage.title} className="timeline__step">
            <div className="timeline__rail">
              <span className="timeline__icon">
                <ContentIcon name={stage.icon} size={16} />
              </span>
              {index < effect.timeline.length - 1 && <span className="timeline__line" />}
            </div>
            <div className="timeline__body">
              <div className="timeline__title">{stage.title}</div>
              <div className="timeline__text">{stage.body}</div>
            </div>
          </li>
        ))}
      </ol>
      <p className="onb__caption">{effect.caption}</p>
    </>
  );
}

export function WhyStep() {
  return (
    <>
      <h1 className="onb__title">{why.title}</h1>
      <div className="why-grid">
        {why.cards.map((card) => (
          <div key={card.title} className="why-card">
            <div className="why-card__icon">
              <ContentIcon name={card.icon} size={22} />
            </div>
            <div className="why-card__title">{card.title}</div>
            <div className="why-card__body">{card.body}</div>
          </div>
        ))}
      </div>
    </>
  );
}

/** Экран 7: что сохранили. Пустые поля не показываются — анкету могли заполнить частично. */
export function DoneStep({ profile }: { profile: Profile }) {
  const rows = [
    profile.goal ? { label: done.goal, value: GOAL_LABELS[profile.goal] } : null,
    profile.heightCm !== null ? { label: done.height, value: `${profile.heightCm} см` } : null,
    profile.weightKg !== null ? { label: done.weight, value: `${profile.weightKg} кг` } : null,
    profile.bmi !== null ? { label: done.bmi, value: formatBmi(profile.bmi) } : null,
  ].filter((row) => row !== null);

  return (
    <div className="onb__center">
      <div className="success">
        <CheckCircle2 size={44} strokeWidth={1.6} aria-hidden />
      </div>
      <h1 className="onb__title">{done.title}</h1>
      <p className="onb__subtitle">{done.body}</p>
      {rows.length > 0 && (
        <dl className="summary">
          {rows.map((row) => (
            <div key={row.label} className="summary__row">
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <ProductShot size="wide" />
    </div>
  );
}
